import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useRelocations } from './useRelocations';

/**
 * Estado y datos del listado de reubicaciones (seguimiento).
 * Convierte condiciones de filtro en parámetros del GET /relocations.
 */
export const useRelocationsMain = (externalFilterConditions?: FilterConditionOutput[]) => {
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);

  const DEFAULT_COLUMNS: string[] = ['location_order_id', 'cart_id', 'status', 'dispatched_at', 'created_at'];
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_COLUMNS);

  const handleColumnsChange = useCallback((newSelectedColumns: string[]) => {
    setSelectedColumns(newSelectedColumns);
  }, []);

  const processedFilters = useMemo(() => {
    try {
      const conditionsToProcess = externalFilterConditions || filterConditions;
      let status: string | undefined;
      let cart_id: number | undefined;
      let date_from: string | undefined;
      let date_to: string | undefined;

      conditionsToProcess.forEach((condition) => {
        if (condition.column === 'status' && condition.value !== undefined && condition.value !== '') {
          if (Array.isArray(condition.value)) {
            status = String(condition.value[0]);
          } else {
            status = String(condition.value);
          }
        }
        if (condition.column === 'cart_id' && condition.value !== undefined && condition.value !== '') {
          const raw = Array.isArray(condition.value) ? condition.value[0] : condition.value;
          const n = Number(raw);
          if (!Number.isNaN(n)) cart_id = n;
        }
        if (condition.column === 'created_at' && condition.action === 'greater_equal' && condition.value) {
          date_from = String(condition.value);
        }
        if (condition.column === 'created_at' && condition.action === 'less_equal' && condition.value) {
          date_to = String(condition.value);
        }
      });

      return {
        ...(status && { status }),
        ...(cart_id !== undefined && { cart_id }),
        ...(date_from && { date_from }),
        ...(date_to && { date_to }),
      };
    } catch {
      return {};
    }
  }, [filterConditions, externalFilterConditions]);

  const { relocations, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useRelocations(processedFilters);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const relocationsViewProps = useMemo(
    () => ({
      relocations,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting: null,
    }),
    [relocations, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, total, refetch],
  );

  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0;
  }, [filterConditions.length]);

  return {
    filterConditions,
    selectedColumns,
    relocations,
    isLoading,
    isFetching,
    error,
    total,
    relocationsViewProps,
    handleFilterChange,
    handleColumnsChange,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
  };
};
